import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import {
  getCustomFieldDefinitions,
  createCustomFieldDefinition,
  updateCustomFieldDefinition,
  deleteCustomFieldDefinition
} from '../../services/customFieldsApi';
import { CustomFieldDefinition, FieldType, FIELD_TYPE_CONFIG } from '../../types/custom-fields';
import toast from 'react-hot-toast';
import ConfirmationDialog from '../ConfirmationDialog';
import Button from '@shared/ui/Button';
import FormModal from '@shared/ui/FormModal';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../App';
import { EditIcon, ErrorIcon } from '../icons';
import ActionsHeaderHelp from '../ActionsHeaderHelp';
import { HelpTrigger } from '../../help';
import Chip from '../Chip';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from '../StandardTable';

interface CustomFieldsPanelProps {
  organizationId: string;
  organizationName: string;
}

const CustomFieldsPanel: React.FC<CustomFieldsPanelProps> = ({ 
  organizationId, 
  organizationName 
}) => {
  const [definitions, setDefinitions] = useState<CustomFieldDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState<CustomFieldDefinition | null>(null);
  const [deletingDefinition, setDeletingDefinition] = useState<CustomFieldDefinition | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  
  useEffect(() => {
    if (organizationId) {
      fetchDefinitions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);
  
  const fetchDefinitions = async () => {
    if (!organizationId) return;
    
    try {
      setLoading(true);
      const data = await getCustomFieldDefinitions(organizationId);
      setDefinitions(data);
    } catch (error) {
      toast.error('Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreate = () => {
    setEditingDefinition(null);
    setShowCreateModal(true);
  };
  
  const handleEdit = (definition: CustomFieldDefinition) => {
    setEditingDefinition(definition);
    setShowCreateModal(true);
  };
  
  const handleDelete = async () => {
    if (!deletingDefinition) return;
    
    try {
      await deleteCustomFieldDefinition(deletingDefinition.id);
      toast.success('Custom field deleted successfully');
      setDeletingDefinition(null);
      await fetchDefinitions(); // Await the refresh to update the list immediately
    } catch (error) {
      toast.error('Failed to delete custom field');
    }
  };
  
  const handleSave = async () => {
    // Refresh after modal closes
    await fetchDefinitions();
    setShowCreateModal(false);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newDefinitions = [...definitions];
    const draggedItem = newDefinitions[draggedIndex];
    newDefinitions.splice(draggedIndex, 1);
    newDefinitions.splice(index, 0, draggedItem);

    setDefinitions(newDefinitions);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null) return;

    try {
      // Update display_order for all fields sequentially to avoid race conditions
      for (let index = 0; index < definitions.length; index++) {
        await updateCustomFieldDefinition(definitions[index].id, {
          display_order: index
        });
      }

      // Wait for database transactions to commit
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Refresh the list to get the saved order from backend (cache-busting via timestamp in API)
      await fetchDefinitions();
      
      toast.success('Field order updated');
    } catch (error) {
      toast.error('Failed to update field order');
      // Revert to original order on error
      await fetchDefinitions();
    } finally {
      setDraggedIndex(null);
    }
  };
  
  /* No early return while loading: StandardTable renders its own loading state,
     so the heading and New Field button stay put instead of the whole panel
     swapping to a "Loading..." line and back. */
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
              Custom Fields
            </h3>
            <HelpTrigger contentKey="org-custom-fields" />
          </div>
          <p className="text-text-secondary dark:text-text-dark-secondary mt-1">
            Create and manage custom field definitions for {organizationName}
          </p>
        </div>
        <Button
          onClick={handleCreate}
          variant="primary"
              className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          New Field
        </Button>
      </div>
      
      {/* Custom Fields table.
          Drag-to-reorder lives on the row itself, with the handle in its own
          narrow first column — the field order here is the order these render
          on entity detail pages, so it has to stay draggable. */}
      <StandardTable
        loading={loading}
        emptyMessage="No custom fields yet — create one to start tracking your own data on bills, legislators, and other entities."
      >
        <StandardTableHeader>
          <StandardTableHeaderCell columnType="narrow">
            <span className="sr-only">Reorder</span>
          </StandardTableHeaderCell>
          <StandardTableHeaderCell>Field</StandardTableHeaderCell>
          <StandardTableHeaderCell columnType="fit">Type</StandardTableHeaderCell>
          <StandardTableHeaderCell responsiveHide="md">Applies to</StandardTableHeaderCell>
          <StandardTableHeaderCell columnType="fit" responsiveHide="lg">
            Options
          </StandardTableHeaderCell>
          <StandardTableHeaderCell columnType="fit">Status</StandardTableHeaderCell>
          <StandardTableHeaderCell columnType="actions" align="right">
            <span className="inline-flex items-center gap-1.5">
              Actions
              <ActionsHeaderHelp icons={['edit', 'delete']} />
            </span>
          </StandardTableHeaderCell>
        </StandardTableHeader>

        <StandardTableBody>
          {definitions.map((definition, index) => (
            <StandardTableRow
              key={definition.id}
              className={draggedIndex === index ? 'opacity-50' : undefined}
            >
              <StandardTableCell>
                <span
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className="inline-flex cursor-move text-text-tertiary dark:text-text-dark-tertiary"
                  aria-label={`Reorder ${definition.name}`}
                  title="Drag to reorder"
                >
                  <GripVertical className="h-4 w-4" />
                </span>
              </StandardTableCell>

              <StandardTableCell>
                <div className="font-medium text-text-primary dark:text-text-dark-primary">
                  {definition.name}
                </div>
                {definition.description && (
                  <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
                    {definition.description}
                  </div>
                )}
              </StandardTableCell>

              <StandardTableCell>
                <Chip
                  size="sm"
                  tone="info"
                  label={
                    FIELD_TYPE_CONFIG[definition.field_type]?.displayName || definition.field_type
                  }
                />
              </StandardTableCell>

              <StandardTableCell responsiveHide="md">
                <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                  {definition.applicable_entity_types && definition.applicable_entity_types.length > 0
                    ? definition.applicable_entity_types.join(', ')
                    : 'All entity types'}
                </span>
              </StandardTableCell>

              <StandardTableCell responsiveHide="lg">
                <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                  {definition.field_type === 'select' || definition.field_type === 'multi_select'
                    ? definition.field_config.options?.length || 0
                    : '—'}
                </span>
              </StandardTableCell>

              <StandardTableCell>
                {definition.is_active ? (
                  <Chip size="sm" tone="positive" label="Active" />
                ) : (
                  <Chip size="sm" tone="negative" label="Inactive" />
                )}
              </StandardTableCell>

              <StandardTableCell align="right">
                <div className="flex items-center justify-end gap-1">
                  <button
                    onClick={() => handleEdit(definition)}
                    className="p-2 text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary rounded transition-colors"
                    title="Edit field"
                    aria-label={`Edit ${definition.name}`}
                  >
                    <EditIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeletingDefinition(definition)}
                    className="p-2 text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 rounded transition-colors"
                    title="Delete field"
                    aria-label={`Delete ${definition.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </StandardTableCell>
            </StandardTableRow>
          ))}
        </StandardTableBody>
      </StandardTable>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <CustomFieldFormModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={handleSave}
          definition={editingDefinition}
          organizationId={organizationId}
        />
      )}
      
      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={!!deletingDefinition}
        onConfirm={handleDelete}
        onCancel={() => setDeletingDefinition(null)}
        title="Delete Custom Field"
        message={`Are you sure you want to delete "${deletingDefinition?.name}"? All values for this field will also be deleted. This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

// Custom Field Form Modal Component
interface CustomFieldFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  definition: CustomFieldDefinition | null;
  organizationId: string;
}

const CustomFieldFormModal: React.FC<CustomFieldFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  definition,
  organizationId
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fieldType, setFieldType] = useState<FieldType>('text');
  const [options, setOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState('');
  const [applicableEntityTypes, setApplicableEntityTypes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  /* The entities a field can attach to come from the note_entity_types registry —
     the same one notes use, since both attach user content to the same entities —
     not a list held here. The two drifted apart, and the dialog's own labels
     disagreed with the registry's (issue #1136). */
  const { data: entityTypes = [] } = useQuery({
    queryKey: ['custom-field-entity-types'],
    queryFn: async () => {
      const res = await api.get('/custom-fields/entity-types');
      return (res.data?.data?.entityTypes ?? []) as Array<{
        name: string;
        display_name: string;
      }>;
    },
    staleTime: 30 * 60 * 1000,
  });
  
  useEffect(() => {
    if (definition) {
      setName(definition.name);
      setDescription(definition.description || '');
      setFieldType(definition.field_type);
      setOptions(definition.field_config?.options || []);
      setApplicableEntityTypes(definition.applicable_entity_types || []);
    } else {
      setName('');
      setDescription('');
      setFieldType('text');
      setOptions([]);
      setApplicableEntityTypes([]);
    }
  }, [definition, isOpen]);
  
  const handleSubmit = async () => {
    setError(null);
    
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    
    if ((fieldType === 'select' || fieldType === 'multi_select') && options.length === 0) {
      setError('At least one option is required for select fields');
      return;
    }
    
    setSaving(true);
    
    try {
      const fieldConfig: any = {};
      
      if (fieldType === 'select' || fieldType === 'multi_select') {
        fieldConfig.options = options;
      } else if (fieldType === 'currency') {
        fieldConfig.currency_code = 'USD';
        fieldConfig.decimal_places = 2;
        fieldConfig.prefix = '$';
      }
      
      if (definition) {
        // Update existing
        await updateCustomFieldDefinition(definition.id, {
          name,
          description,
          field_config: fieldConfig,
          applicable_entity_types: applicableEntityTypes.length > 0 ? applicableEntityTypes : undefined
        });
        toast.success('Custom field updated successfully');
      } else {
        // Create new
        await createCustomFieldDefinition({
          organization_id: organizationId,
          name,
          description,
          field_type: fieldType,
          field_config: fieldConfig,
          applicable_entity_types: applicableEntityTypes.length > 0 ? applicableEntityTypes : undefined
        });
        toast.success('Custom field created successfully');
      }
      
      onSave();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save custom field');
    } finally {
      setSaving(false);
    }
  };
  
  const addOption = () => {
    if (newOption.trim() && !options.includes(newOption.trim())) {
      setOptions([...options, newOption.trim()]);
      setNewOption('');
    }
  };
  
  const removeOption = (option: string) => {
    setOptions(options.filter(o => o !== option));
  };
  
  const toggleEntityType = (entityType: string) => {
    setApplicableEntityTypes(prev =>
      prev.includes(entityType)
        ? prev.filter(t => t !== entityType)
        : [...prev, entityType]
    );
  };
  
  return (
    <FormModal
      isOpen={isOpen}
      title={definition ? 'Edit custom field' : 'Create custom field'}
      onClose={onClose}
      onSubmit={handleSubmit}
      size="lg"
      submitLabel={definition ? 'Update' : 'Create'}
      isSubmitting={saving}
    >
      <div className="space-y-4">
          <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                <ErrorIcon className="h-5 w-5 text-error-600 dark:text-error-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
              </div>
            )}
            
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2">
                Field Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Priority, Cost Impact, Internal Status"
                className="w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary"
                required
              />
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this field is used for"
                rows={2}
                className="w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary"
              />
            </div>
            
            {/* Field Type */}
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2">
                Field Type <span className="text-red-600">*</span>
              </label>
              <select
                value={fieldType}
                onChange={(e) => setFieldType(e.target.value as FieldType)}
                disabled={!!definition} // Can't change type after creation
                className="w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary"
              >
                {Object.entries(FIELD_TYPE_CONFIG).map(([type, config]) => (
                  <option key={type} value={type}>
                    {config.displayName} - {config.description}
                  </option>
                ))}
              </select>
              {definition && (
                <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                  Field type cannot be changed after creation
                </p>
              )}
            </div>
            
            {/* Options (for select types) */}
            {(fieldType === 'select' || fieldType === 'multi_select') && (
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2">
                  Options <span className="text-red-600">*</span>
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                    placeholder="Add an option"
                    className="flex-1 px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary"
                  />
                  <Button
                    type="button"
                    onClick={addOption}
                    variant="secondary"
                  >
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {options.map((option) => (
                    <div key={option} className="flex items-center justify-between p-2 bg-background-tertiary dark:bg-background-dark-tertiary rounded">
                      <span className="text-text-primary dark:text-text-dark-primary">{option}</span>
                      <button
                        type="button"
                        onClick={() => removeOption(option)}
                        className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Applicable Entity Types */}
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2">
                Applicable To
              </label>
              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-2">
                Leave empty to allow this field on all entity types
              </p>
              <div className="space-y-2">
                {entityTypes.map((type) => (
                  <label key={type.name} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={applicableEntityTypes.includes(type.name)}
                      onChange={() => toggleEntityType(type.name)}
                      className="h-4 w-4 text-primary-700 dark:text-primary-600 border-border-light dark:border-border-dark rounded"
                    />
                    <span className="ml-2 text-text-secondary dark:text-text-dark-secondary">
                      {type.display_name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          
          {/* Footer */}
      </div>
    </FormModal>
  );
};

export default CustomFieldsPanel;
